import { useCallback } from 'react';
import { DOCUMENT, PAY_FREQUENCY } from '@config/constants';
import { useAreDocumentsReady } from '@views/ProofDocuments/utils';
import {
  useDocumentForm,
  useFindDocumentByGroup,
} from '@views/ProofDocuments/hooks';
import { useGetDocumentsInfoQuery } from '../../graphql';
import { ts } from '../utils';

export const useController = () => {
  const {
    control,
    resetForm,
    clearField,
    onUploadError,
    onUploadCompleted: onUploadCompletedForm,
  } = useDocumentForm();

  const {
    data,
    loading: isLoading,
    refetch: refetchDocuments,
  } = useGetDocumentsInfoQuery({
    notifyOnNetworkStatusChange: true,
  });
  const incomePeriodicity = data?.activeRequisition?.income?.paymentFrequency;

  const descriptionText = PAY_FREQUENCY.WEEKLY === incomePeriodicity ? ts('descriptionWeekly') : ts('description');

  const { document: Monthly1Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.LAST_PERIOD,
  });

  const { document: Monthly2Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.ONE_PERIOD_AGO,
  });

  const { document: ForthNight1Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.LAST_PERIOD,
  });

  const { document: ForthNight2Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.ONE_PERIOD_AGO,
  });

  const { document: ForthNight3Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.TWO_PERIODS_AGO,
  });

  const { document: ForthNight4Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.THREE_PERIODS_AGO,
  });

  const { document: Weekly1Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.LAST_PERIOD,
  });

  const { document: Weekly2Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.ONE_PERIOD_AGO,
  });

  const { document: Weekly3Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.TWO_PERIODS_AGO,
  });

  const { document: Weekly4Document } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.THREE_PERIODS_AGO,
  });

  const { document: OtherDocument } = useFindDocumentByGroup({
    data,
    klass: DOCUMENT.CLASS.PROOF_OF_INCOME,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.OTHER,
  });

  let neededDocuments;

  if (incomePeriodicity === PAY_FREQUENCY.WEEKLY) {
    neededDocuments = [
      Weekly1Document,
      Weekly2Document,
      Weekly3Document,
      Weekly4Document,
    ];
  }

  if (incomePeriodicity === PAY_FREQUENCY.BIWEEKLY) {
    neededDocuments = [
      ForthNight1Document,
      ForthNight2Document,
      ForthNight3Document,
      ForthNight4Document,
    ];
  }

  if (incomePeriodicity === PAY_FREQUENCY.MONTHLY) {
    neededDocuments = [Monthly1Document, Monthly2Document];
  }

  const areDocumentsReady = useAreDocumentsReady({ neededDocuments });

  const onRemoveCompleted = useCallback(() => {
    resetForm();
    refetchDocuments();
  }, [
    resetForm,
    refetchDocuments,
  ]);

  const onUploadCompleted = useCallback(({ fieldName }) => {
    onUploadCompletedForm({ fieldName });
    refetchDocuments();
  }, [
    refetchDocuments,
    onUploadCompletedForm,
  ]);

  return {
    control,
    isLoading,
    clearField,
    onUploadError,
    OtherDocument,
    descriptionText,
    Weekly1Document,
    Weekly2Document,
    Weekly3Document,
    Weekly4Document,
    Monthly1Document,
    Monthly2Document,
    areDocumentsReady,
    incomePeriodicity,
    onRemoveCompleted,
    onUploadCompleted,
    ForthNight1Document,
    ForthNight2Document,
    ForthNight3Document,
    ForthNight4Document,
  };
};
